import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { EDITOR_ACTIONS, createEditorState, editorReducer, isEditorDirty } from './editorState.js';

/**
 * Edit-mode session for one dashboard (Beta-1 plan P3-16): Customize → edit a copy → Done
 * (hands the layout to `onDone`, P3-19 saves it there) or Cancel (restores the snapshot).
 * Also carries the remove/undo pair the snackbar shows.
 *
 *   const editor = useDashboardEditor(initialLayout, { onDone: save });
 *   <EditModeControls editor={editor} onAdd={() => setGalleryOpen(true)} />
 *   <WidgetGallery open={galleryOpen} editor={editor} registry={registry} role={role} dashboard="drew-oe" onClose={…} />
 *   <WidgetGrid layout={editor.layout} editing={editor.editing} onLayoutChange={editor.setLayout}
 *               onRemove={editor.remove} onRequestEdit={editor.begin} … />
 *
 * `initialLayout` is the loaded layout; when it changes identity while NOT editing (a load from
 * Firestore, a reset to the role default) the editor replaces its copy. While editing, loads
 * are ignored so the user's work is not clobbered.
 */
export function useDashboardEditor(initialLayout, { onDone, onCancel } = {}) {
  const [state, dispatch] = useReducer(editorReducer, initialLayout, createEditorState);
  const editingRef = useRef(state.editing);
  editingRef.current = state.editing;

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!editingRef.current) dispatch({ type: EDITOR_ACTIONS.REPLACE, layout: initialLayout });
  }, [initialLayout]);

  const begin = useCallback(() => dispatch({ type: EDITOR_ACTIONS.BEGIN }), []);
  const setLayout = useCallback((layout) => dispatch({ type: EDITOR_ACTIONS.SET_LAYOUT, layout }), []);
  const remove = useCallback((i, title) => dispatch({ type: EDITOR_ACTIONS.REMOVE, i, title }), []);
  const undoRemove = useCallback(() => dispatch({ type: EDITOR_ACTIONS.UNDO_REMOVE }), []);
  const dismissUndo = useCallback(() => dispatch({ type: EDITOR_ACTIONS.DISMISS_UNDO }), []);
  const replace = useCallback((layout) => dispatch({ type: EDITOR_ACTIONS.REPLACE, layout }), []);
  const add = useCallback((widget, { size, props } = {}) => dispatch({ type: EDITOR_ACTIONS.ADD, widget, size, props }), []);
  const clearAdded = useCallback(() => dispatch({ type: EDITOR_ACTIONS.CLEAR_ADDED }), []);

  const stateRef = useRef(state);
  stateRef.current = state;
  const done = useCallback(() => {
    const current = stateRef.current;
    if (!current.editing) return;
    dispatch({ type: EDITOR_ACTIONS.DONE });
    if (onDone) onDone(current.layout, { dirty: isEditorDirty(current) });
  }, [onDone]);
  const cancel = useCallback(() => {
    const current = stateRef.current;
    if (!current.editing) return;
    dispatch({ type: EDITOR_ACTIONS.CANCEL });
    if (onCancel) onCancel(current.snapshot);
  }, [onCancel]);

  return useMemo(
    () => ({
      layout: state.layout,
      editing: state.editing,
      dirty: isEditorDirty(state),
      removed: state.removed,
      lastAdded: state.lastAdded,
      begin,
      done,
      cancel,
      setLayout,
      replace,
      remove,
      undoRemove,
      dismissUndo,
      add,
      clearAdded,
    }),
    [state, begin, done, cancel, setLayout, replace, remove, undoRemove, dismissUndo, add, clearAdded]
  );
}

export default useDashboardEditor;
